/**
 * Care journey derivation — single source of truth for the canonical
 * patient journey steps, shared by the patient portal ("My care journey"
 * on PatientDashboard) and the clinical Patient 360 workspace.
 *
 * Pure function over already-fetched data — no API calls here.
 */
import { fmtDate } from '../dashboard/shared';

const up = (v) => String(v || '').toUpperCase();

/**
 * @param {{ requests?: any[], carePlans?: any[], visits?: any[] }} data
 * @returns {{ steps: { key:string, label:string, caption:string, state:'done'|'current'|'pending' }[], hasAny: boolean }}
 */
export function deriveCareJourney(data = {}) {
  const requests  = Array.isArray(data.requests)  ? data.requests  : [];
  const carePlans = Array.isArray(data.carePlans) ? data.carePlans : [];
  const visits    = Array.isArray(data.visits)    ? data.visits    : [];

  const now = new Date();

  const firstRequest = [...requests].sort((a, b) =>
    new Date(a.created_at || a.requested_at || 0) - new Date(b.created_at || b.requested_at || 0))[0];
  const firstPlan = [...carePlans].sort((a, b) =>
    new Date(a.start_date || a.created_at || 0) - new Date(b.start_date || b.created_at || 0))[0];

  const assignedRequest = requests.find(r =>
    r.assigned_nurse_id || r.nurse_id || r.assigned_nurse_name || r.nurse_name);
  const firstVisit = [...visits].sort((a, b) =>
    new Date(a.scheduled_start || 0) - new Date(b.scheduled_start || 0))[0];

  const activePlan = carePlans.find(c => up(c.status) === 'ACTIVE');
  const completedPlan = carePlans.find(c => up(c.status) === 'COMPLETED');
  const reviewPlan = carePlans.find(c => c.review_date || c.end_date);

  const upcomingVisit = visits.find(v => {
    const st = up(v.status);
    return !['CANCELLED', 'NO_SHOW'].includes(st) &&
      v.scheduled_start && new Date(v.scheduled_start) >= now;
  });

  const nurseName = firstVisit?.nurse_name || assignedRequest?.nurse_name || assignedRequest?.assigned_nurse_name;

  // ── Step completion rules ────────────────────────────────────────────────
  const done = [
    requests.length > 0,                                   // 1 request submitted
    carePlans.length > 0,                                  // 2 assessment & planning
    visits.length > 0 || Boolean(assignedRequest),         // 3 nurse assigned
    Boolean(upcomingVisit) || Boolean(activePlan),         // 4 treatment in progress
    Boolean(completedPlan),                                // 5 review — pending unless COMPLETED
    Boolean(completedPlan),                                // 6 discharge
  ];

  const captions = [
    firstRequest
      ? `Requested ${fmtDate(firstRequest.created_at || firstRequest.requested_at)}`
      : 'Submit a service request to begin',
    firstPlan
      ? (firstPlan.diagnosis || firstPlan.title || firstPlan.plan_name || `Plan started ${fmtDate(firstPlan.start_date || firstPlan.created_at)}`)
      : 'Your doctor will assess and plan your care',
    nurseName
      ? `Nurse ${nurseName}`
      : (done[2] ? 'A nurse has been assigned' : 'A nurse will be matched to you'),
    upcomingVisit
      ? `Next visit ${fmtDate(upcomingVisit.scheduled_start)}`
      : (activePlan ? 'Care plan active' : 'Visits will be scheduled'),
    reviewPlan
      ? `Review ${fmtDate(reviewPlan.review_date || reviewPlan.end_date)}`
      : 'Your care team reviews progress regularly',
    completedPlan
      ? `Plan completed${completedPlan.end_date ? ` ${fmtDate(completedPlan.end_date)}` : ''}`
      : 'Discharge once your care plan completes',
  ];

  const labels = [
    'Service request submitted',
    'Assessment & care planning',
    'Nurse assigned',
    'Treatment in progress',
    'Care plan review',
    'Discharge',
  ];

  // Current step = first not-done step (only meaningful if anything exists).
  const firstPending = done.findIndex(d => !d);
  const hasAny = requests.length > 0 || carePlans.length > 0 || visits.length > 0;

  const steps = labels.map((label, i) => ({
    key: `step-${i + 1}`,
    label,
    caption: captions[i],
    state: done[i] ? 'done' : (hasAny && i === firstPending ? 'current' : 'pending'),
  }));

  return { steps, hasAny };
}

export default deriveCareJourney;
